import { useNavigate } from '@tanstack/react-router';
import { memo, useCallback, useContext } from 'react';
import { Dialog, OverlayTriggerStateContext, Tabs } from 'react-aria-components';

import {
  Button,
  CopyText,
  Heading,
  Modal,
  ModalOverlay,
  Separator,
  Tab,
  TabList,
  TabPanel,
  Text,
} from '@/components/core';
import { TRIGGERS } from '@/components/flow';
import { CloseIcon, EmailIcon, SaveIcon } from '@/components/icons';
import { ExcelLogo, PowerAutomateLogo, WebhookLogo } from '@/components/logos';
import { CREATE_AGENT_RUN_FROM_EMAIL_FUNCTION_ID } from '@/constants';
import { useAddTrigger, useAgentTree } from '@/hooks';

import { UploadDocumentsDropzone } from './UploadDocumentsDropzone.tsx';

export type ImportModalProps = {
  agentId: string;
};

const IImportModal = ({ agentId }: ImportModalProps) => {
  const { actions } = useAgentTree({ agentId });
  const overlayTriggerState = useContext(OverlayTriggerStateContext);
  const navigate = useNavigate();
  const email = actions.find((action) => action.functionId === CREATE_AGENT_RUN_FROM_EMAIL_FUNCTION_ID)?.config
    .email as string | undefined;

  const emailTrigger = TRIGGERS.find((t) => t.functionId === CREATE_AGENT_RUN_FROM_EMAIL_FUNCTION_ID);
  const { addTrigger, isPending: isPendingAddEmailTrigger } = useAddTrigger();

  const onAddEmailTrigger = useCallback(() => {
    if (emailTrigger) {
      addTrigger({ agentId, triggerOption: emailTrigger });
    }
  }, [addTrigger, agentId, emailTrigger]);

  return (
    <ModalOverlay>
      <Modal>
        <Dialog className="outline-none">
          {({ close }) => (
            <div className="flex flex-col items-center gap-2 p-4">
              <div className="flex w-full flex-row items-center gap-2">
                <Heading>Upload files to agent</Heading>
                <div className="grow" />
                <Button variant="plain" onPress={close}>
                  <CloseIcon />
                </Button>
              </div>

              <div className="p-1">
                <UploadDocumentsDropzone
                  className="h-48 w-[500px]"
                  agentId={agentId}
                  onUploadSuccess={() => overlayTriggerState?.close()}
                />
              </div>
              <div className="gap-sm flex items-center">
                <Separator orientation="horizontal" className="w-[160px]" />
                <Text className="font-medium">Or import automatically via</Text>
                <Separator orientation="horizontal" className="w-[160px]" />
              </div>
              <Tabs className="w-full">
                <TabList className="py-2">
                  <Tab className="grow justify-center" id="email">
                    <EmailIcon />
                    <Text>Email</Text>
                  </Tab>
                  <Tab className="grow justify-center" id="import">
                    <div className="gap-sm flex -translate-x-6 items-center">
                      <div className="gap-sm flex">
                        <ExcelLogo className="translate-x-8" size="md" />
                        <PowerAutomateLogo className="z-10 translate-x-4" size="md" />
                        <WebhookLogo className="z-20" size="md" />
                      </div>
                      <Text>Integrations</Text>
                    </div>
                  </Tab>
                </TabList>
                <TabPanel id="email">
                  <div className="flex w-full flex-col items-start gap-2 py-2">
                    <Text className="text-xs font-medium text-gray-900">
                      Send documents to the Cradl AI email inbox
                    </Text>
                    {email ? (
                      <CopyText className="w-full" value={email} />
                    ) : (
                      <div className="flex w-full items-center rounded-md border border-gray-200 py-1 pr-1 pl-3">
                        <Text className="text-xs text-gray-500">Add email trigger to generate email address</Text>
                        <div className="grow" />
                        <Button variant="secondary" onPress={onAddEmailTrigger} isDisabled={isPendingAddEmailTrigger}>
                          {isPendingAddEmailTrigger ? <SaveIcon /> : null}
                          <Text>Add trigger</Text>
                        </Button>
                      </div>
                    )}
                  </div>
                </TabPanel>
                <TabPanel id="import">
                  <div className="flex w-full flex-col items-start gap-2 rounded-lg border border-gray-200 p-3">
                    <Heading>Integrate</Heading>
                    <Text className="font-regular text-gray-600">
                      Automatically import files from automation platforms or email
                    </Text>
                    <Button
                      className="px-0"
                      variant="plain"
                      onPress={() => navigate({ to: '/agents/$agentId/integration', params: { agentId } })}
                    >
                      <Text className="!text-primary-500">Go to workflow</Text>
                    </Button>
                  </div>
                </TabPanel>
              </Tabs>
            </div>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};

export const ImportModal = memo(IImportModal) as typeof IImportModal;
